import { useMemo } from "react";
import type { Message } from "@langchain/langgraph-sdk";
import { extractToolCalls } from "../primitives/AIMessage";
import { extractText } from "../attachments/types";
import { useToolLabels } from "../primitives/ToolLabelsContext";
import type { TodoItem, TodoDelegation } from "../primitives/TodoList";

// ─────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────

/**
 * Display category for a tool. Categories drive the aggregate tallies
 * ("Read 3 · Edited 2 · Searched 4") and the per-row verb/icon in the
 * ToolActivity card. Consumers map tool names to categories via
 * `ToolLabels.categories`; unmapped tools land in the generic `work`
 * bucket so a newly-added tool is visible by default rather than
 * silently dropped.
 */
export type ToolCategoryName =
  | "read"
  | "search"
  | "edit"
  | "create"
  | "remove"
  | "delegate"
  | "work";

export type ToolActivityStatus = "running" | "complete" | "error";

export interface ToolActivityItem {
  id: string;
  name: string;
  args?: Record<string, unknown>;
  status: ToolActivityStatus;
}

/**
 * A contiguous burst of tool activity. Groups span messages: a
 * multi-step agent turn (AI[tool_use] → Tool → AI[tool_use] → Tool …)
 * folds into ONE group so the UI shows a single accumulating card
 * instead of a chip per call. Assistant text closes the current group —
 * prose is a narrative beat, and activity after it is a new burst.
 */
export interface ToolActivityGroup {
  /**
   * id of the AI message whose tool calls opened the group. Consumers
   * render the card at this message's position in the transcript.
   */
  anchorMessageId: string;
  items: ToolActivityItem[];
}

/**
 * Universal tool-name → category mappings for the tools every agent
 * framework surface shares (deepagents filesystem/task tools, MCP
 * universal tools). Consumers extend/override via
 * `ToolLabels.categories` for bespoke tools.
 */
export const DEFAULT_TOOL_CATEGORIES: Record<string, ToolCategoryName> = {
  read_file: "read",
  ls: "read",
  glob: "search",
  grep: "search",
  search: "search",
  describe: "search",
  list_resources: "search",
  write_file: "create",
  edit_file: "edit",
  delete_file: "remove",
  task: "delegate",
  execute: "work",
  call: "work",
};

/**
 * Resolve a tool name to its display category. Consumer mappings win;
 * unknown tools land in the generic `work` bucket.
 */
export function categoryForTool(
  name: string,
  categories?: Record<string, ToolCategoryName>
): ToolCategoryName {
  return categories?.[name] ?? DEFAULT_TOOL_CATEGORIES[name] ?? "work";
}

// ─────────────────────────────────────────────────────────────────────────
// Derivation (pure)
// ─────────────────────────────────────────────────────────────────────────

export interface DeriveToolActivityOptions {
  /** Tool names to exclude from activity entirely (same semantics as
   * `ToolLabels.hidden` — their output is its own UI). */
  hidden?: readonly string[];
}

/**
 * Walk a message array and derive tool-activity groups, keyed by the
 * anchor message id.
 *
 * Grouping rules:
 *   - A human message closes the open group.
 *   - An AI message with visible text closes the open group; its OWN
 *     tool calls (if any) open a new group anchored at that message —
 *     matching the visual order "prose first, then the activity it
 *     kicked off".
 *   - An AI message with tool calls but no text appends to the open
 *     group (or opens one anchored at itself).
 *   - ToolMessages never break a group; they resolve item status by
 *     `tool_call_id` (complete, or error when `status === "error"`).
 *   - Any LATER message (AI or human) settles everything still
 *     unresolved before it: the agent only produces its next message
 *     after the previous step's tools return, so "the agent moved on"
 *     IS the completion signal. This matters because parallel tool
 *     batches emit their ToolMessages only at the join, and some
 *     transports don't deliver ToolMessages mid-run at all — without
 *     this, an old burst spins forever while later beats stream.
 *
 * Items for hidden tools never appear. Internal `extract-<n>` calls are
 * already dropped by `extractToolCalls`.
 */
export function deriveToolActivityGroups(
  messages: Message[],
  options?: DeriveToolActivityOptions
): Map<string, ToolActivityGroup> {
  const hidden =
    options?.hidden && options.hidden.length ? new Set(options.hidden) : null;

  const groups = new Map<string, ToolActivityGroup>();
  const itemsById = new Map<string, ToolActivityItem>();
  let open: ToolActivityGroup | null = null;

  // "The agent moved on" — mark every still-running item complete.
  const settleUnresolved = () => {
    for (const item of itemsById.values()) {
      if (item.status === "running") item.status = "complete";
    }
  };

  for (const message of messages) {
    if (message.type === "human") {
      settleUnresolved();
      open = null;
      continue;
    }

    if (message.type === "tool") {
      const toolCallId = (message as { tool_call_id?: string }).tool_call_id;
      const item = toolCallId ? itemsById.get(toolCallId) : undefined;
      if (item) {
        const status = (message as { status?: string }).status;
        item.status = status === "error" ? "error" : "complete";
      }
      continue;
    }

    if (message.type !== "ai") continue;

    // A new AI message proves the previous step's tools returned —
    // settle them BEFORE registering this message's own calls.
    settleUnresolved();

    const hasText = Boolean(extractText(message.content)?.trim());
    if (hasText) open = null;

    const toolCalls = extractToolCalls(message).filter(
      (tc) => !hidden?.has(tc.name)
    );
    if (toolCalls.length === 0) continue;

    if (!open) {
      open = { anchorMessageId: message.id ?? "", items: [] };
      groups.set(open.anchorMessageId, open);
    }
    for (const tc of toolCalls) {
      const item: ToolActivityItem = {
        id: tc.id,
        name: tc.name,
        args: tc.args,
        status: "running",
      };
      open.items.push(item);
      itemsById.set(tc.id, item);
    }
  }

  return groups;
}

// ─────────────────────────────────────────────────────────────────────────
// React hook
// ─────────────────────────────────────────────────────────────────────────

/**
 * Memoized tool-activity groups for a message array, with the hidden
 * list sourced from the ambient `ToolLabelsProvider`.
 *
 * @example
 * ```tsx
 * const activity = useToolActivity(messages);
 * // in the render loop:
 * const group = message.id ? activity.get(message.id) : undefined;
 * {group && <Chat.ToolActivity group={group} isStreaming={isStreaming} />}
 * ```
 */
export function useToolActivity(
  messages: Message[]
): Map<string, ToolActivityGroup> {
  const { hidden } = useToolLabels();
  return useMemo(
    () => deriveToolActivityGroups(messages, { hidden }),
    [messages, hidden]
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Agent activity — tool bursts joined to the todo checklist
// ─────────────────────────────────────────────────────────────────────────

/**
 * A `task` dispatch reads its own completion from the todos channel, not
 * from a ToolMessage. Parallel task batches only emit their ToolMessages
 * when the whole batch joins, so a finished section would keep showing
 * as in-flight while its siblings run. Flip any running `task` row whose
 * `todo_id` is already completed to `complete`.
 */
function syncTaskItemsWithTodos(
  group: ToolActivityGroup,
  completedTodoIds: Set<string>
): ToolActivityGroup {
  if (completedTodoIds.size === 0) return group;
  let changed = false;
  const items = group.items.map((item) => {
    const todoId =
      typeof item.args?.todo_id === "string" ? item.args.todo_id : undefined;
    if (
      item.name === "task" &&
      item.status === "running" &&
      todoId &&
      completedTodoIds.has(todoId)
    ) {
      changed = true;
      return { ...item, status: "complete" as const };
    }
    return item;
  });
  return changed ? { ...group, items } : group;
}

/**
 * Everything a chat surface needs to render the agent's live work as one
 * cohesive unit — the tool-activity feed joined to the todo checklist.
 *
 * `useToolActivity` stays the low-level primitive (raw groups by anchor);
 * this hook layers on the joins that every surface was re-deriving by
 * hand: todo-completion sync, delegation extraction, the current-turn
 * live burst, and the "anything still running" flag.
 */
export interface AgentActivityState {
  /** Tool-activity groups keyed by anchor message id, todo-synced. */
  byMessage: Map<string, ToolActivityGroup>;
  /** All groups in message order. */
  groups: ToolActivityGroup[];
  /**
   * Anchor id of the burst that belongs to the current, still-streaming
   * turn — the one a surface hoists into its trailing AgentActivity card
   * alongside the todos. `undefined` once the run settles or when the
   * only groups are from prior turns.
   */
  currentGroupId?: string;
  /** The current-turn burst object, for convenience. */
  currentGroup?: ToolActivityGroup;
  /** `task` dispatches that reference a todo, for TodoList attribution. */
  delegations: TodoDelegation[];
  /** True while any tool call is still in flight and the stream is open. */
  isLive: boolean;
}

export interface UseAgentActivityOptions {
  messages: Message[];
  /** The todos channel, if this surface has one. */
  todos?: TodoItem[];
  /** Whether the stream is currently open. */
  isStreaming?: boolean;
}

export function useAgentActivity({
  messages,
  todos,
  isStreaming = false,
}: UseAgentActivityOptions): AgentActivityState {
  const byMessageRaw = useToolActivity(messages);
  const { agents } = useToolLabels();

  return useMemo(() => {
    const completedTodoIds = new Set(
      (todos ?? [])
        .filter((t) => t.status === "completed" && t.id)
        .map((t) => t.id!)
    );

    const byMessage = new Map<string, ToolActivityGroup>();
    for (const [id, group] of byMessageRaw) {
      byMessage.set(id, syncTaskItemsWithTodos(group, completedTodoIds));
    }
    const groups = Array.from(byMessage.values());

    const isLive =
      isStreaming &&
      groups.some((g) => g.items.some((i) => i.status === "running"));

    // The current burst is the last group anchored AFTER the last human
    // message: it belongs to the turn in progress, so its feed hoists
    // into the trailing card with the todos instead of sitting inline.
    let lastHumanIndex = -1;
    const indexById = new Map<string, number>();
    messages.forEach((m, i) => {
      if (m.id) indexById.set(m.id, i);
      if (m.type === "human") lastHumanIndex = i;
    });
    let currentGroupId: string | undefined;
    const last = groups[groups.length - 1];
    if (isStreaming && last) {
      const idx = indexById.get(last.anchorMessageId);
      if (idx !== undefined && idx > lastHumanIndex) {
        currentGroupId = last.anchorMessageId;
      }
    }
    const currentGroup = currentGroupId
      ? byMessage.get(currentGroupId)
      : undefined;

    const delegations: TodoDelegation[] = groups.flatMap((group) =>
      group.items
        .filter((item) => item.name === "task")
        .map((item) => {
          const type =
            typeof item.args?.subagent_type === "string"
              ? item.args.subagent_type
              : undefined;
          return {
            todoId:
              typeof item.args?.todo_id === "string"
                ? item.args.todo_id
                : undefined,
            agent: (type && agents?.[type]) ?? "Helper",
            running: isStreaming && item.status === "running",
          };
        })
    );

    return {
      byMessage,
      groups,
      currentGroupId,
      currentGroup,
      delegations,
      isLive,
    };
  }, [byMessageRaw, todos, isStreaming, agents, messages]);
}
